import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Plus, Trash2, UserRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import PasswordInput from "@/components/PasswordInput";

const btnGhost =
  "inline-flex items-center gap-1 rounded-full border border-[#E3EBF3] px-2.5 py-1 text-[10px] font-bold text-slate-600 transition-colors duration-200 hover:bg-[#EFF6FC] hover:text-[#1479BF]";
const btnPrimary =
  "rounded-full bg-[#1479BF] px-5 py-2 text-[11px] font-bold tracking-[0.08em] text-white transition-colors duration-200 hover:bg-[#0B2440] disabled:opacity-50";
const inputCls =
  "w-full rounded-lg border border-[#E3EBF3] px-3 py-2 text-sm outline-none transition focus:border-[#1479BF]";

export default function PpidAccountsView({ user }) {
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState("");
  const [pwTarget, setPwTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [usernameTarget, setUsernameTarget] = useState(null);
  const [newUsername, setNewUsername] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [create, setCreate] = useState({ label: "", username: "", password: "" });

  const load = useCallback(async () => {
    try {
      setAccounts(await api("/api/admin/ppid-accounts"));
    } catch (e) {
      toast(formatApiError(e.message));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const changePassword = async (acc) => {
    setBusy(`pw-${acc.key}`);
    try {
      await api(`/api/admin/ppid-accounts/${acc.key}/password`, {
        method: "POST",
        body: JSON.stringify({ password: newPassword }),
      });
      toast.success(`Password ${acc.label} diperbarui`);
      setPwTarget(null);
      setNewPassword("");
      await load();
    } catch (e) {
      toast(formatApiError(e.message));
    } finally {
      setBusy("");
    }
  };

  const changeUsername = async (acc) => {
    setBusy(`un-${acc.key}`);
    try {
      await api(`/api/admin/ppid-accounts/${acc.key}/username`, {
        method: "POST",
        body: JSON.stringify({ username: newUsername }),
      });
      toast.success("Username berhasil diubah");
      setUsernameTarget(null);
      setNewUsername("");
      await load();
    } catch (e) {
      toast(formatApiError(e.message));
    } finally {
      setBusy("");
    }
  };

  const createAccount = async () => {
    setBusy("create");
    try {
      await api("/api/admin/ppid-accounts", {
        method: "POST",
        body: JSON.stringify(create),
      });
      toast.success("Akun PPID baru tersimpan");
      setCreate({ label: "", username: "", password: "" });
      setShowCreate(false);
      await load();
    } catch (e) {
      toast(formatApiError(e.message));
    } finally {
      setBusy("");
    }
  };

  const removeAccount = async (acc) => {
    setBusy(`del-${acc.key}`);
    try {
      await api(`/api/admin/ppid-accounts/${acc.key}`, { method: "DELETE" });
      toast.success(`Akun ${acc.label} dihapus`);
      await load();
    } catch (e) {
      toast(formatApiError(e.message));
    } finally {
      setBusy("");
    }
  };

  return (
    <section data-testid="ppid-accounts" className="mt-10 rounded-3xl border border-[#E3EBF3] bg-white p-6 md:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl font-semibold text-[#0B2440]">
          Akun PPID
        </h2>
        {user?.key === "ppid" && (
          <button
            data-testid="ppid-accounts-create"
            onClick={() => setShowCreate((s) => !s)}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1479BF] px-5 py-2 text-[11px] font-bold tracking-[0.08em] text-white transition-colors duration-300 hover:bg-[#0B2440]"
          >
            <Plus aria-hidden="true" className="h-3.5 w-3.5" />
            TAMBAH AKUN
          </button>
        )}
      </div>
      <p className="mt-2 text-xs leading-relaxed text-slate-400">
        Ganti password akun PPID mana pun, serta username &amp; password akun
        Anda sendiri. Username akun PPID lain hanya dapat diubah oleh
        pemiliknya. Tambah/hapus akun hanya untuk Admin utama (login email).
      </p>

      {showCreate && user?.key === "ppid" && (
        <div
          data-testid="ppid-accounts-create-form"
          className="mt-4 grid gap-3 rounded-2xl border border-dashed border-[#E3EBF3] p-4 md:grid-cols-3"
        >
          <input
            data-testid="ppid-create-label"
            placeholder="Nama (mis. Surya)"
            value={create.label}
            onChange={(e) => setCreate((c) => ({ ...c, label: e.target.value }))}
            className={inputCls}
          />
          <input
            data-testid="ppid-create-username"
            placeholder="Username (tanpa spasi)"
            value={create.username}
            onChange={(e) => setCreate((c) => ({ ...c, username: e.target.value }))}
            className={inputCls}
          />
          <PasswordInput
            testid="ppid-create-password"
            placeholder="Password (min. 6 karakter)"
            value={create.password}
            onChange={(e) => setCreate((c) => ({ ...c, password: e.target.value }))}
            className={inputCls}
          />
          <div>
            <button
              data-testid="ppid-create-save"
              onClick={createAccount}
              disabled={
                busy === "create" ||
                !create.label.trim() ||
                !create.username.trim() ||
                !create.password
              }
              className={btnPrimary}
            >
              SIMPAN AKUN
            </button>
          </div>
        </div>
      )}

      <div className="mt-5 space-y-3">
        {accounts.map((a) => (
          <div
            key={a.key}
            data-testid={`ppid-account-${a.key}`}
            className="rounded-2xl border border-[#E3EBF3] px-4 py-3"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#0B2440]">
                  {a.label}
                  {a.is_primary && (
                    <span className="ml-2 rounded-full bg-[#E8A33D]/15 px-2 py-0.5 text-[9px] font-bold text-[#B97A18]">
                      UTAMA — GOOGLE
                    </span>
                  )}
                  {a.is_me && (
                    <span className="ml-2 rounded-full bg-[#1479BF]/10 px-2 py-0.5 text-[9px] font-bold text-[#1479BF]">
                      ANDA
                    </span>
                  )}
                </p>
                <p className="truncate text-xs text-slate-400">
                  {a.is_primary
                    ? a.email || "Login dengan Google"
                    : `Username: ${a.username || "-"}`}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {!a.is_primary && (
                  <>
                    {a.is_me && (
                      <button
                        data-testid={`ppid-account-username-${a.key}`}
                        onClick={() => {
                          setUsernameTarget(usernameTarget === a.key ? null : a.key);
                          setNewUsername(a.username || "");
                        }}
                        className={btnGhost}
                      >
                        <UserRound aria-hidden="true" className="h-3.5 w-3.5" />
                        Ganti Username
                      </button>
                    )}
                    <button
                      data-testid={`ppid-account-password-${a.key}`}
                      onClick={() => {
                        setPwTarget(pwTarget === a.key ? null : a.key);
                        setNewPassword("");
                      }}
                      className={btnGhost}
                    >
                      <KeyRound aria-hidden="true" className="h-3.5 w-3.5" />
                      Ganti Password
                    </button>
                    {user?.key === "ppid" && !a.is_me && (
                      <button
                        data-testid={`ppid-account-delete-${a.key}`}
                        onClick={() => removeAccount(a)}
                        disabled={busy === `del-${a.key}`}
                        className="inline-flex items-center gap-1 rounded-full border border-[#E3EBF3] px-2.5 py-1 text-[10px] font-bold text-slate-400 transition-colors duration-200 hover:border-red-200 hover:bg-red-50 hover:text-red-500"
                      >
                        <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
                        Hapus
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {pwTarget === a.key && !a.is_primary && (
              <div
                data-testid={`ppid-pw-form-${a.key}`}
                className="mt-3 flex flex-wrap items-center gap-2"
              >
                <div className="min-w-[220px] flex-1">
                  <PasswordInput
                    testid={`ppid-pw-input-${a.key}`}
                    autoComplete="new-password"
                    placeholder="Password baru (min. 6 karakter)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={inputCls}
                  />
                </div>
                <button
                  data-testid={`ppid-pw-save-${a.key}`}
                  onClick={() => changePassword(a)}
                  disabled={busy === `pw-${a.key}` || !newPassword}
                  className={btnPrimary}
                >
                  SIMPAN PASSWORD
                </button>
              </div>
            )}

            {usernameTarget === a.key && a.is_me && !a.is_primary && (
              <div
                data-testid={`ppid-username-form-${a.key}`}
                className="mt-3 flex flex-wrap items-center gap-2"
              >
                <div className="min-w-[220px] flex-1">
                  <input
                    data-testid={`ppid-username-input-${a.key}`}
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    placeholder="Username baru (tanpa spasi)"
                    className={inputCls}
                  />
                </div>
                <button
                  data-testid={`ppid-username-save-${a.key}`}
                  onClick={() => changeUsername(a)}
                  disabled={busy === `un-${a.key}` || !newUsername.trim()}
                  className={btnPrimary}
                >
                  SIMPAN USERNAME
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}